"use client";

import { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "interactive" | "bordered";
  padding?: "none" | "sm" | "md" | "lg";
  children: ReactNode;
}

const paddingStyles = {
  none: "",
  sm: "p-[14px]",
  md: "p-5",
  lg: "p-[26px]",
};

const variantStyles = {
  default: "border border-border",
  interactive:
    "rounded-[11px] border border-border cursor-pointer transition-[border-color,background,transform] duration-[220ms] hover:border-brand hover:bg-card-hover hover:-translate-y-px",
  bordered:
    "border border-border transition-[border-color] duration-[220ms] hover:border-brand-glow-30",
};

function Card({
  variant = "default",
  padding = "md",
  children,
  className = "",
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        "bg-card rounded-[14px]",
        paddingStyles[padding],
        variantStyles[variant],
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function CardHeader({ children, className = "", ...props }: CardHeaderProps) {
  return (
    <div className={cn("mb-4", className)} {...props}>
      {children}
    </div>
  );
}

interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode;
}

function CardTitle({ children, className = "", ...props }: CardTitleProps) {
  return (
    <h3
      className={cn(
        "text-lg font-semibold tracking-[-0.025em] text-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  );
}

interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
  children: ReactNode;
}

function CardDescription({
  children,
  className = "",
  ...props
}: CardDescriptionProps) {
  return (
    <p
      className={cn(
        "text-sm text-muted-foreground mt-1 leading-relaxed",
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
}

interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function CardContent({ children, className = "", ...props }: CardContentProps) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function CardFooter({ children, className = "", ...props }: CardFooterProps) {
  return (
    <div className={cn("mt-5 flex items-center gap-2.5", className)} {...props}>
      {children}
    </div>
  );
}

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  type CardProps,
};
